import type {
	PermissionIdentifier,
	PermissionRequirement,
} from '@/interfaces/permission.interface';

type PermissionObject = Extract<PermissionIdentifier, { resource: string; action: string }>;

const isPermissionObject = (value: unknown): value is PermissionObject =>
	typeof value === 'object' &&
	value !== null &&
	'resource' in (value as Record<string, unknown>) &&
	'action' in (value as Record<string, unknown>);

const toPermissionCode = (identifier: PermissionIdentifier): string =>
	isPermissionObject(identifier) ? `${identifier.resource}.${identifier.action}` : identifier;

const toPermissionSet = (permissions: Iterable<string>): Set<string> => {
	if (permissions instanceof Set) {
		return permissions;
	}

	return new Set(Array.from(permissions));
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getAnyOf = (requirement: PermissionRequirement): PermissionIdentifier[] | null => {
	if (!isRecord(requirement)) {
		return null;
	}

	const anyOf = (requirement as { anyOf?: unknown }).anyOf;
	return Array.isArray(anyOf) ? (anyOf as PermissionIdentifier[]) : null;
};

const getAllOf = (requirement: PermissionRequirement): PermissionIdentifier[] | null => {
	if (!isRecord(requirement)) {
		return null;
	}

	const allOf = (requirement as { allOf?: unknown }).allOf;
	return Array.isArray(allOf) ? (allOf as PermissionIdentifier[]) : null;
};

export const satisfiesPermissionRequirement = (
	permissions: Iterable<string>,
	requirement?: PermissionRequirement,
): boolean => {
	if (!requirement) {
		return true;
	}

	if (typeof requirement === 'string' || isPermissionObject(requirement)) {
		const permissionSet = toPermissionSet(permissions);
		return permissionSet.has(toPermissionCode(requirement));
	}

	const anyOf = getAnyOf(requirement);
	if (anyOf) {
		const permissionSet = toPermissionSet(permissions);
		return anyOf.some(identifier =>
			permissionSet.has(toPermissionCode(identifier as PermissionIdentifier)),
		);
	}

	const allOf = getAllOf(requirement);
	if (allOf) {
		const permissionSet = toPermissionSet(permissions);
		return allOf.every(identifier =>
			permissionSet.has(toPermissionCode(identifier as PermissionIdentifier)),
		);
	}

	return false;
};

export const permissionKey = (resource: string, action: string) => `${resource}.${action}`;
